import { addDays, shiftMonths, weekdayOf } from '@sitzmark/core';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { isoDay } from '../lib/date-range.js';
import { monthGrid } from './date-grid.js';
import './date-input.css';

/**
 * A date box with the store's own calendar (client, 2026-10-16: "no styling
 * should use chrome default dialogues, looks unprofessional, should always be
 * our css styling … check that we aren't using default browser css for any
 * dialogues or dropdowns anywhere").
 *
 * **The typing stays the browser's; the calendar is ours.** A date input's
 * month / day / year segments are the best way there is to *type* a date —
 * each digit lands in its place, in the machine's own order, and a browser
 * journey fills it with `2026-10-01` — so they stay, in the store's box. What
 * the browser drew on top was its own grey calendar, opened from its own icon
 * and styled by nobody. That icon is hidden; this button opens a calendar
 * drawn from the tokens, in the top layer so a modal's scroll box cannot clip
 * it.
 *
 * The calendar is a grid of days (the ARIA grid pattern): arrows move a day or
 * a week, Page Up / Page Down a month, Home / End the week's ends, Enter
 * picks, Escape closes and gives the caret back to the box. Alt+Down or F4 in
 * the box opens it, as they would the browser's. Days outside `min` and `max`
 * show but cannot be picked — the box keeps the same limits.
 *
 * The only `<input type="date">` in the register: `scripts/check-invariants.mjs`
 * refuses one anywhere else, so the browser's calendar cannot come back by a
 * screen forgetting.
 */
export function DateInput({
  id,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  value,
  onChange,
  min,
  max,
  disabled = false,
  className,
}: {
  id?: string;
  'aria-describedby'?: string | undefined;
  'aria-label'?: string;
  /** YYYY-MM-DD, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) field.current?.focus();
  };
  return (
    <span className={`date-input${className === undefined ? '' : ` ${className}`}`} ref={wrap}>
      <input
        ref={field}
        id={id}
        type="date"
        className="input date-input__field"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if ((event.altKey && event.key === 'ArrowDown') || event.key === 'F4') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      />
      <button
        type="button"
        className="date-input__open"
        aria-label="Choose from a calendar"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        // The box's own reason covers its button.
        aria-describedby={disabled ? describedBy : undefined}
        onClick={() => setOpen((was) => !was)}
      >
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
          <rect
            x="3"
            y="4.5"
            width="14"
            height="12"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="M3 8.5h14M7 2.75v3.5M13 2.75v3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open ? (
        <Calendar
          anchor={wrap}
          value={value}
          min={min}
          max={max}
          onPick={(day) => {
            onChange(day);
            close(true);
          }}
          onClose={close}
        />
      ) : null}
    </span>
  );
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const asDate = (day: string) => new Date(`${day}T12:00:00Z`);
const longDay = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const monthTitle = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function Calendar({
  anchor,
  value,
  min,
  max,
  onPick,
  onClose,
}: {
  anchor: RefObject<HTMLSpanElement | null>;
  value: string;
  min?: string | undefined;
  max?: string | undefined;
  onPick: (day: string) => void;
  onClose: (refocus: boolean) => void;
}) {
  const reasonId = useId();
  const today = isoDay(new Date());
  const allowed = (day: string) =>
    (min === undefined || min === '' || day >= min) &&
    (max === undefined || max === '' || day <= max);
  const [focused, setFocused] = useState(() =>
    ISO_DAY.test(value)
      ? value
      : allowed(today)
        ? today
        : max !== undefined && max !== '' && today > max
          ? max
          : (min ?? today),
  );
  const panel = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // In the top layer, under its box (above it when there is no room below), on screen.
  useLayoutEffect(() => {
    const element = panel.current;
    const box = anchor.current?.getBoundingClientRect();
    if (element === null || box === undefined) return;
    if (typeof element.showPopover === 'function') element.showPopover();
    const gutter = 8;
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const left = Math.max(gutter, Math.min(box.left, window.innerWidth - width - gutter));
    const below = box.bottom + 4;
    const above = box.top - height - 4;
    const top = below + height > window.innerHeight - gutter && above > gutter ? above : below;
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
  }, [anchor]);

  // The focused day holds the caret, so the arrow keys walk the grid.
  useEffect(() => {
    grid.current?.querySelector<HTMLElement>(`[data-day="${focused}"]`)?.focus();
  }, [focused]);

  // A press anywhere else, a page scroll or a resize closes it: it is a moment, not a place.
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      const path = event.composedPath();
      if (panel.current !== null && path.includes(panel.current)) return;
      if (anchor.current !== null && path.includes(anchor.current)) return;
      closeRef.current(false);
    };
    const away = (event: Event) => {
      const target = event.target;
      if (panel.current !== null && target instanceof Node && panel.current.contains(target))
        return;
      closeRef.current(false);
    };
    const resized = () => closeRef.current(false);
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', resized);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', resized);
    };
  }, [anchor]);

  const month = focused.slice(0, 7);
  const weeks = monthGrid(focused);

  const keys = (event: ReactKeyboardEvent) => {
    const step: Record<string, () => string> = {
      ArrowLeft: () => addDays(focused, -1),
      ArrowRight: () => addDays(focused, 1),
      ArrowUp: () => addDays(focused, -7),
      ArrowDown: () => addDays(focused, 7),
      PageUp: () => shiftMonths(focused, -1),
      PageDown: () => shiftMonths(focused, 1),
      Home: () => addDays(focused, -weekdayOf(focused)),
      End: () => addDays(focused, 6 - weekdayOf(focused)),
    };
    const next = step[event.key];
    if (next !== undefined) {
      event.preventDefault();
      setFocused(next());
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (allowed(focused)) onPick(focused);
    }
  };

  return (
    <div
      ref={panel}
      className="date-input__calendar"
      role="dialog"
      aria-label="Choose a date"
      popover="manual"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onClose(true);
        }
      }}
    >
      <div className="date-input__head">
        <button
          type="button"
          className="date-input__step"
          aria-label="Previous month"
          onClick={() => setFocused(shiftMonths(focused, -1))}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
            <path
              d="M10 3.5 5.5 8 10 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="date-input__month" aria-live="polite">
          {monthTitle.format(asDate(focused))}
        </span>
        <button
          type="button"
          className="date-input__step"
          aria-label="Next month"
          onClick={() => setFocused(shiftMonths(focused, 1))}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
            <path
              d="M6 3.5 10.5 8 6 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div
        ref={grid}
        role="grid"
        className="date-input__grid"
        aria-label={monthTitle.format(asDate(focused))}
        onKeyDown={keys}
      >
        <div role="row" className="date-input__row">
          {WEEKDAYS.map((name) => (
            <span key={name} role="columnheader" className="date-input__weekday">
              {name}
            </span>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0]} role="row" className="date-input__row">
            {week.map((day) => {
              const can = allowed(day);
              return (
                <span
                  key={day}
                  role="gridcell"
                  data-day={day}
                  tabIndex={day === focused ? 0 : -1}
                  aria-selected={day === value}
                  aria-disabled={can ? undefined : true}
                  aria-describedby={can ? undefined : reasonId}
                  aria-current={day === today ? 'date' : undefined}
                  aria-label={longDay.format(asDate(day))}
                  className={[
                    'date-input__day',
                    day.slice(0, 7) === month ? '' : 'date-input__day--outside',
                    day === value ? 'date-input__day--chosen' : '',
                    day === today ? 'date-input__day--today' : '',
                    can ? '' : 'date-input__day--closed',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => (can ? onPick(day) : setFocused(day))}
                >
                  {Number(day.slice(8))}
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <span id={reasonId} className="visually-hidden">
        Outside the dates this box allows
      </span>
      <div className="date-input__foot">
        {allowed(today) ? (
          <button type="button" className="date-input__link" onClick={() => onPick(today)}>
            Today
          </button>
        ) : (
          <span />
        )}
        {value !== '' ? (
          <button type="button" className="date-input__link" onClick={() => onPick('')}>
            Clear
          </button>
        ) : null}
      </div>
    </div>
  );
}
